
class VADDetector {
  constructor(options = {}) {
    // Internal state
    this.audioCtx = null;
    this.analyser = null;
    this.source = null;
    this.data = null;
    this.raf = null;

    // External analyser support
    this.externalAnalyser = options.useExternalAnalyser || null;

    // Speech state
    this.isSpeaking = false;
    this.lastSpoke = 0;
    this.smoothValue = 0;

    // Configuration
    this.threshold = options.threshold || 0.06;
    this.silenceTimeout = options.silenceTimeout || 500;
    this.smoothing = options.smoothing || 0.80;

    // Callbacks
    this.onSpeechStart = options.onSpeechStart || (() => {});
    this.onSpeechEnd = options.onSpeechEnd || (() => {});
    this.onAudioLevel = options.onAudioLevel || (() => {});
  }

  /**
   * Initialize VAD with audio stream + optional external analyser
   */
  async init(stream, externalAnalyser) {
    try {
      // CASE 1: Using external analyser
      if (externalAnalyser) {
        this.analyser = externalAnalyser;

        // Resume AudioContext if suspended
        const ctx = externalAnalyser.context;
        if (ctx && ctx.state === "suspended") {
          await ctx.resume();
        }

        this.data = new Float32Array(this.analyser.fftSize);
        return true;
      }

      // CASE 2: Create internal analyser
      this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      await this.audioCtx.resume();

      this.analyser = this.audioCtx.createAnalyser();
      this.analyser.fftSize = 2048;

      this.source = this.audioCtx.createMediaStreamSource(stream);
      this.source.connect(this.analyser);

      this.data = new Float32Array(this.analyser.fftSize);

      return true;
    } catch (err) {
      console.error("VAD init error:", err);
      return false;
    }
  }

  /**
   * Start VAD loop
   */
  start() {
    if (!this.analyser) return;

    this.isSpeaking = false;
    this.smoothValue = 0;
    this.lastSpoke = performance.now();

    this.tick();
  }

  /**
   * Stop VAD loop
   */
  stop() {
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
  }

  /**
   * DESTROY — Don't stop mic tracks here!
   * Only clear internal objects.
   */
  destroy() {
    this.stop();

    //  NEVER STOP MIC TRACKS HERE
    // The parent component owns the stream, not VAD.

    this.audioCtx = null;
    this.analyser = null;
    this.data = null;
    this.source = null;
  }

  /**
   * Calculate RMS (root mean square)
   */
  rms(buf) {
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
    return Math.sqrt(sum / buf.length);
  }

  /**
   * Main detection loop
   */
  tick = () => {
    if (!this.analyser) return;

    this.analyser.getFloatTimeDomainData(this.data);
    const level = this.rms(this.data);

    // Smooth the RMS values
    this.smoothValue =
      this.smoothing * this.smoothValue + (1 - this.smoothing) * level;

    // Emit audio-level callback
    this.onAudioLevel(this.smoothValue);

    const now = performance.now();

    // Speech detection
    if (this.smoothValue > this.threshold) {
      this.lastSpoke = now;

      if (!this.isSpeaking) {
        this.isSpeaking = true;
        this.onSpeechStart();
      }
    } else {
      if (this.isSpeaking && now - this.lastSpoke > this.silenceTimeout) {
        this.isSpeaking = false;
        this.onSpeechEnd();
      }
    }

    this.raf = requestAnimationFrame(this.tick);
  };
}

export default VADDetector;
